import { AlertCircle, InfoCircle, XClose } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import ErrorMsg from 'src/components/errorMsg';
import TooltipCmp from 'src/components/TooltipCmp';
import { REGULAR_EXPRESSION_NEW } from 'src/constants/common';

export interface IInputTextProps {
    inputName?: string | undefined;
    placeholder?: any | undefined;
    parentClassName?: string;
    errorText?: any;
    inputType?: 'text' | 'number' | 'email' | 'password';
    label?: string | React.ReactElement | undefined;
    value?: number | string | undefined;
    id?: string | undefined;
    icon?: any;
    iconLeft?: any;
    labelIconRight?: any;
    autoFocus?: boolean;
    tabIndex?: number | undefined;
    className?: string | undefined;
    labelClassName?: string | undefined;
    required?: boolean;
    requiredAfter?: boolean | undefined;
    disabled?: boolean;
    isClearable?: boolean;
    onChangeFunc?: (e: any) => void;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleKeyDown?: (e: React.KeyboardEvent) => void;
    readOnly?: boolean;
    isError?: boolean;
    isLoading?: boolean;
    searchIconClass?: string;
    inputWrapClass?: string;
    isShowAiStar?: boolean;
    onKeyPress?: (e: React.KeyboardEvent) => void; // need to clean it up
    inputMainClassName?: string;
    inputValueUnitType?: string;
    shouldFormatNumber?: boolean;
    requiredClassName?: string;
    labelWrapClass?: string;
    isShowDisableClass?: boolean;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    // validations
    isAllowZeroValue?: boolean;
    maxLength?: number | undefined;
    isAllowDecimal?: boolean;
    isAllowNegative?: boolean;
    allowZeroToEnter?: boolean; // Add this new prop
    min?: string | number | undefined;
    // tooltip props
    tooltipMessage?: any;
    tooltipOpenOnClick?: boolean;
    tooltipFollowCursor?: boolean;
    tooltipParentClassName?: string;
    tooltipChildrenClassName?: string;
    tooltipIsArrow?: boolean;
    tooltipCustomClass?: string;
    tooltipStyle?: React.CSSProperties;
    size?: string;
    isIconError?: boolean;
    autoComplete?: string;
}

const defaultNumberMaxLength = 6;
const defaultTextMaxLength = 50;
const defaultEmailMaxLength = 100;

const InputText = ({
    id,
    inputName = '',
    placeholder = '',
    errorText = '',
    className = '',
    inputWrapClass,
    label = '',
    tabIndex,
    icon = '',
    autoComplete = 'off',
    isIconError = false,
    iconLeft,
    labelIconRight,
    disabled,
    onChangeFunc = () => void 0,
    onBlur = () => void 0,
    onFocus,
    handleKeyDown,
    required,
    requiredAfter,
    readOnly = false,
    value,
    labelClassName = '',
    parentClassName,
    isClearable,
    isError,
    isLoading,
    autoFocus,
    searchIconClass,
    isShowAiStar,
    inputMainClassName,
    requiredClassName,
    shouldFormatNumber = false,
    inputValueUnitType = '',
    labelWrapClass,
    isShowDisableClass = true,
    onPaste,
    // validations
    inputType = 'text',
    isAllowZeroValue = false,
    maxLength = undefined,
    isAllowDecimal = false,
    isAllowNegative = false,
    allowZeroToEnter = false, // Add this new prop
    min,
    // tooltip props
    tooltipMessage,
    tooltipOpenOnClick,
    tooltipFollowCursor,
    tooltipParentClassName = '',
    tooltipChildrenClassName = '',
    tooltipIsArrow = true,
    tooltipCustomClass = '',
    tooltipStyle,
    size,
}: IInputTextProps) => {
    const [isFocused, setIsFocused] = useState(false);

    const HandleChange = (e: any) => {
        let regularExpToTest = /^.*$/; // default value if not match with any of the below cases

        if (inputType === 'email') {
            regularExpToTest = REGULAR_EXPRESSION_NEW.TEXT(maxLength ?? defaultEmailMaxLength);
        } else if (inputType === 'text') {
            regularExpToTest = REGULAR_EXPRESSION_NEW.TEXT(maxLength ?? defaultTextMaxLength);
        } else if (inputType === 'number') {
            if (isAllowDecimal) {
                if (isAllowNegative) {
                    regularExpToTest = REGULAR_EXPRESSION_NEW.DIGIT_DECIMAL_WITH_NEGATIVE(maxLength ?? defaultNumberMaxLength);
                } else {
                    regularExpToTest = REGULAR_EXPRESSION_NEW.DIGIT_DECIMAL(maxLength ?? defaultNumberMaxLength);
                }
            } else {
                if (isAllowNegative) {
                    regularExpToTest = REGULAR_EXPRESSION_NEW.DIGIT_ABSOLUTE_WITH_NEGATIVE(maxLength ?? defaultNumberMaxLength);
                } else {
                    // Pass the allowZeroToEnter parameter
                    regularExpToTest = REGULAR_EXPRESSION_NEW.DIGIT_ABSOLUTE(maxLength ?? defaultNumberMaxLength, allowZeroToEnter);
                }
            }
        }

        const valueTypeMatch = regularExpToTest.test(String(e.target.value));

        // Skip this check if allowZeroToEnter is true
        if (inputType === 'number' && !allowZeroToEnter) {
            if ((value == '0' && Number(e.target.value) > 0) || (value == '-0' && Number(e.target.value) < 0)) {
                onChangeFunc({
                    target: {
                        ...e?.target,
                        name: inputName,
                        value: Number(e.target.value),
                    },
                });
            }
        }

        if (e.target.value === '' || valueTypeMatch) {
            onChangeFunc(e);
        }
    };

    const clearInput = () => {
        onChangeFunc({ target: { name: inputName, value: '' } });
    };

    const handleFocus = (e: any) => {
        setTimeout(() => {
            setIsFocused(true);
        }, 10);
        if (onFocus) onFocus(e);
    };

    const handleBlur = (e: any) => {
        setIsFocused(false);

        if (isAllowZeroValue && inputType === 'number' && e?.target?.value === '0') {
            onChangeFunc({
                target: { value: 0, name: inputName },
            });
        } else {
            const formattedNumber = inputType === 'number' ? Number(e?.target?.value) || '' : e?.target?.value || '';

            if (shouldFormatNumber && formattedNumber !== value && value !== '') {
                onChangeFunc({
                    target: { value: formattedNumber, name: inputName },
                });
            }
        }
        if (onBlur) onBlur(e);
    };

    const formatNumber = (num: string | number | null | undefined) => {
        if (num === null || num === undefined || num === '') return '';
        const numericValue = Number(num);
        if (isNaN(numericValue)) return '';

        return new Intl.NumberFormat('en-US').format(numericValue);
    };

    const handleInputKeyDown = (e: any) => {
        if (handleKeyDown) {
            handleKeyDown(e);
        }
    };

    return (
        <>
            <div className={`${!isIconError && errorText && typeof errorText !== 'boolean' ? ' mb-2' : ''} ${parentClassName}`}>
                <div className={`${inputMainClassName}`}>
                    <div className={`${labelIconRight ? 'flex justify-between' : ''} ${labelWrapClass}`}>
                        {label && (
                            <div className="flex items-center gap-x-1.5">
                                <label className={`form_label ${labelClassName}`}>
                                    {label}
                                    {required && <span className={`text-red-600 ms-[2px] leading-4 text-xs ${requiredClassName}`}>*</span>}
                                </label>
                                {tooltipMessage && (
                                    <TooltipCmp
                                        message={tooltipMessage}
                                        openOnClick={tooltipOpenOnClick}
                                        followCursor={tooltipFollowCursor}
                                        parentClassName={`max-w-[230px] ${tooltipParentClassName}`}
                                        childrenClassName={tooltipChildrenClassName}
                                        isArrow={tooltipIsArrow}
                                        TooltipCustomClass={tooltipCustomClass}
                                        style={tooltipStyle}
                                    >
                                        <InfoCircle className="h-3.5 w-3.5 mb-1.5 cursor-pointer text-gray400 flex-none" />
                                    </TooltipCmp>
                                )}
                            </div>
                        )}
                        {labelIconRight && labelIconRight}
                    </div>
                    {requiredAfter && <span className="text-red-600 ml-0.5 leading-4 text-xs">*</span>}
                    <div
                        className={`relative flex justify-center items-center ${inputWrapClass} ${isLoading ? 'custom-loading rounded-md' : ''} ${
                            !isError && isShowAiStar ? 'ai-gradient-border-wrap' : ''
                        }`}
                    >
                        {iconLeft && iconLeft}

                        <input
                            id={id}
                            type={inputType === 'password' ? 'password' : 'text'}
                            name={inputName}
                            className={`form_control !outline-0 outline-none ${className} ${
                                size === 'sm' ? 'form_control_sm' : size === 'md' ? 'form_control_md' : size === 'lg' ? 'form_control_lg' : ''
                            } ${errorText && 'is-invalid'} ${isError ? 'border-red-600 hover:border-red-600' : ''} ${isShowAiStar ? '' : ''} ${
                                isShowDisableClass ? 'disabled:border-borderPrimary disabled:bg-gray50 disabled:text-gray500 [&:disabled>svg]:hidden disabled-input disabled:opacity-100' : ''
                            }`}
                            placeholder={placeholder}
                            onChange={HandleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleInputKeyDown}
                            onPaste={onPaste}
                            tabIndex={tabIndex}
                            autoFocus={autoFocus}
                            value={shouldFormatNumber && !isFocused ? (formatNumber(value as string) ? `${formatNumber(value)}${inputValueUnitType ? ` ${inputValueUnitType}` : ''}` : '') : value}
                            disabled={disabled}
                            readOnly={readOnly}
                            min={min}
                            autoComplete={autoComplete}
                        />

                        {icon && icon}
                        {isIconError && errorText && typeof errorText !== 'boolean' && (
                            <div className="absolute -translate-y-1/2 top-1/2 right-2 z-[1]">
                                <TooltipCmp
                                    message={errorText}
                                    parentClassName={`max-w-[230px] ${tooltipParentClassName}`}
                                    childrenClassName="block"
                                    isArrow={tooltipIsArrow}
                                    TooltipCustomClass={tooltipCustomClass}
                                    style={tooltipStyle}
                                >
                                    <AlertCircle className="h-4 w-4 text-red-600 cursor-pointer" />
                                </TooltipCmp>
                            </div>
                        )}

                        {isClearable && value !== '' && <XClose id="clear" className={`h-4 w-4 cursor-pointer absolute right-2 clear ${searchIconClass}`} onClick={clearInput} />}
                    </div>
                </div>
                {!isIconError && errorText && typeof errorText !== 'boolean' && <ErrorMsg errorText={errorText} />}
            </div>
        </>
    );
};

export default InputText;
